/**
 * @file RbtTimesheet.jsx
 * @description Standalone SEO page at /rbt-timesheet — targets "RBT timesheet" /
 *   "RBT hours tracker" / "free RBT timesheet" search intent. Explains the free
 *   RBT-specific timesheet built into ABAcases (sessions, paid/unpaid status,
 *   mileage, expenses, multi-agency, notes, CSV/PDF export) from the RBT's
 *   day-to-day angle. Nests under the tracker hub in breadcrumbs. Mirrors the
 *   structure of AboutAbaCases.jsx. Visible copy is in the `rbtTimesheet.*` /
 *   `trackerSeoPages.*` i18n namespaces (EN + ES, runtime switch only); the
 *   SEO surface stays English.
 * @module components/RbtTimesheet
 */
import { useEffect, useState } from "react";
import { T } from "../theme";
import {
  ClockIcon, DollarIcon, CarIcon, FileTextIcon, DownloadIcon, BriefcaseIcon,
  NoteIcon, CalendarIcon, BellIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import QuietEnding from "./QuietEnding";
import { HowItWorksStrip, IconTiles, MoreDetails, DetailList } from "./TrackerVisuals";
import StickyMobileCta from "./StickyMobileCta";
import { setSignupIntent } from "../lib/signupIntent";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import { afterLink } from "../lib/linkText";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

// Card grids: shown as icon tiles, with the long write-ups collapsed under "Details" (kept in the HTML for search).
const GRID_1 = [
  { Icon: ClockIcon, titleKey: "rbtTimesheet.track1Title", bodyKey: "rbtTimesheet.track1Body" },
  { Icon: DollarIcon, titleKey: "rbtTimesheet.track2Title", bodyKey: "rbtTimesheet.track2Body" },
  { Icon: CalendarIcon, titleKey: "rbtTimesheet.track3Title", bodyKey: "rbtTimesheet.track3Body" },
  { Icon: BriefcaseIcon, titleKey: "rbtTimesheet.track4Title", bodyKey: "rbtTimesheet.track4Body" },
];

const GRID_2 = [
  { Icon: ClockIcon, titleKey: "rbtTimesheet.inside1Title", bodyKey: "rbtTimesheet.inside1Body" },
  { Icon: DollarIcon, titleKey: "rbtTimesheet.inside2Title", bodyKey: "rbtTimesheet.inside2Body" },
  { Icon: CarIcon, titleKey: "rbtTimesheet.inside3Title", bodyKey: "rbtTimesheet.inside3Body" },
  { Icon: FileTextIcon, titleKey: "rbtTimesheet.inside4Title", bodyKey: "rbtTimesheet.inside4Body" },
  { Icon: BriefcaseIcon, titleKey: "rbtTimesheet.inside5Title", bodyKey: "rbtTimesheet.inside5Body" },
  { Icon: NoteIcon, titleKey: "rbtTimesheet.inside6Title", bodyKey: "rbtTimesheet.inside6Body" },
  { Icon: DownloadIcon, titleKey: "rbtTimesheet.inside7Title", bodyKey: "rbtTimesheet.inside7Body" },
];

export default function RbtTimesheet({ onNavigate }) {
  const { t } = useTranslation();
  const isEs = String(i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("es");
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // The SEO surface (title, description, canonical, JSON-LD) stays English; only the visible copy follows the language switcher.
  usePageSeo({
    title: "RBT Timesheet & Hours Tracker | ABAcases",
    description: "Track RBT sessions, hours, cancellations, payments, unpaid balances, mileage, and expenses with a free RBT timesheet built for ABA work.",
    canonical: "/rbt-timesheet",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-rbt-timesheet", [
    { name: "Home", url: "/" },
    { name: "Free ABA Timesheet & Payment Tracker", url: "/free-aba-timesheet-payment-tracker" },
    { name: "RBT Timesheet", url: "/rbt-timesheet" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const goJoin = () => {
    track(A.TRACKER_SIGNUP_CLICK, {
      supabaseMeta: { source: "aba_timesheet" },
      ga4Params: ctaMeta({ pagePath: "/rbt-timesheet/", pageType: "tracker", ctaLabel: "Start tracking free", targetPath: "/auth", userLanguage: i18n.language, audience: "tracker" }),
    });
    setSignupIntent("tracker");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  return (
    <div lang={isEs ? "es" : "en"} style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t("trackerSeoPages.crumbHome"), onClick: () => onNavigate("home") },
            { label: t("trackerSeoPages.crumbTracker"), onClick: () => onNavigate("free-aba-timesheet-payment-tracker") },
            { label: t("rbtTimesheet.crumbCurrent") },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t("rbtTimesheet.badge")}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t("rbtTimesheet.h1")}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t("providerPages.trackerLead")}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 28px" }}>
            {t("rbtTimesheet.heroBefore")}{" "}
            <SeoLink to="free-aba-timesheet-payment-tracker" style={{ color: "#fff", fontWeight: 700, borderBottom: "1px solid rgba(255,255,255,0.4)" }}>{t("rbtTimesheet.heroLink")}</SeoLink>{afterLink(t("rbtTimesheet.heroAfter"))}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BellIcon size={16} color="#fff" /> {t("trackerSeoPages.startTracking")}
            </button>
          </div>
        </div>
      </header>

      {/* ── How it works: log, pay date, flagged if late ──────────── */}
      <section style={{ padding: isMobile ? "36px 16px 8px" : "52px 48px 8px" }}>
        <HowItWorksStrip isMobile={isMobile} />
      </section>

      {/* ── 1. Why RBTs need their own timesheet ──────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("rbtTimesheet.whyTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("rbtTimesheet.whyP1")}
          </p>
          <MoreDetails summary={t("trackerVisual.readMore")}>
            <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
              {t("rbtTimesheet.whyP2")}
            </p>
          </MoreDetails>
          <p style={{ fontSize: 14, fontWeight: 700, color: NAVY, background: "#eef2ff", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 14px", margin: "0 0 14px" }}>
            {t("providerPages.clientPhiGuard")}
          </p>
        </div>
      </section>

      {/* ── 2. Why tracking hours and payments matters ────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t("rbtTimesheet.trackTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 18 }}>
            {t("rbtTimesheet.trackP")}
          </p>
          <IconTiles isMobile={isMobile} columns={4} items={GRID_1.map((c) => ({ Icon: c.Icon, title: t(c.titleKey) }))} />
          <MoreDetails summary={t("trackerVisual.details")}>
            <DetailList items={GRID_1.map((c) => ({ title: t(c.titleKey), body: t(c.bodyKey) }))} />
          </MoreDetails>
        </div>
      </section>

      {/* ── 3. What's actually in the RBT timesheet ───────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("rbtTimesheet.insideTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 22 }}>
            {t("rbtTimesheet.insideBefore")}{" "}
            <SeoLink to="free-aba-timesheet-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t("rbtTimesheet.insideLink")}</SeoLink>{afterLink(t("rbtTimesheet.insideAfter"))}
          </p>
          <IconTiles isMobile={isMobile} columns={4} items={GRID_2.map((c) => ({ Icon: c.Icon, title: t(c.titleKey) }))} />
          <MoreDetails summary={t("trackerVisual.details")}>
            <DetailList items={GRID_2.map((c) => ({ title: t(c.titleKey), body: t(c.bodyKey) }))} />
          </MoreDetails>
        </div>
      </section>

      {/* ── 4. Built into the same free profile ───────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("rbtTimesheet.builtTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("rbtTimesheet.builtP1Before")}{" "}
            <SeoLink to="aba-case-finder" style={{ color: INDIGO, fontWeight: 700 }}>{t("rbtTimesheet.builtP1LinkCases")}</SeoLink>{afterLink(t("rbtTimesheet.builtP1Mid"))}{" "}
            <SeoLink to="aba-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t("rbtTimesheet.builtP1LinkPay")}</SeoLink>{afterLink(t("rbtTimesheet.builtP1After"))}
          </p>
          <MoreDetails summary={t("trackerVisual.readMore")}>
            <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
              {t("rbtTimesheet.builtP2")}
            </p>
          </MoreDetails>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t("rbtTimesheet.builtP3Before")}{" "}
            <SeoLink to="aba-timesheet" style={{ color: INDIGO, fontWeight: 700 }}>{t("rbtTimesheet.builtP3Link")}</SeoLink>{afterLink(t("rbtTimesheet.builtP3After"))}
          </p>
        </div>
      </section>

      {/* ── 5. Looking for RBT cases too? ──────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("rbtTimesheet.casesTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("rbtTimesheet.casesP1Before")}{" "}
            <SeoLink to="rbt-jobs-miami-fl" style={{ color: INDIGO, fontWeight: 700 }}>{t("rbtTimesheet.casesLinkMiami")}</SeoLink>,{" "}
            <SeoLink to="rbt-jobs-charlotte-nc" style={{ color: INDIGO, fontWeight: 700 }}>{t("rbtTimesheet.casesLinkCharlotte")}</SeoLink>{afterLink(t("rbtTimesheet.casesOr"))}{" "}
            <SeoLink to="rbt-jobs-north-carolina" style={{ color: INDIGO, fontWeight: 700 }}>{t("rbtTimesheet.casesLinkNc")}</SeoLink>.
          </p>
          <p style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.7, marginTop: 14 }}>
            {t("rbtTimesheet.casesP2Before")}{" "}
            <SeoLink to="how-to-become-an-rbt" style={{ color: INDIGO, fontWeight: 700 }}>{t("rbtTimesheet.casesP2LinkBecome")}</SeoLink>{" "}
            {t("rbtTimesheet.casesP2Mid")}{" "}
            <SeoLink to="rbt-resume-guide" style={{ color: INDIGO, fontWeight: 700 }}>{t("rbtTimesheet.casesP2LinkResume")}</SeoLink>.
          </p>
        </div>
      </section>

      {/* One quiet ending instead of a second CTA band: the hero is this page's only call to action. */}
      <QuietEnding isMobile={isMobile} />

      <PublicFooter />
      <StickyMobileCta label={t("stickyCta.startTracking")} onClick={goJoin} ctaId="tracker" />
    </div>
  );
}
